import { mande, type MandeError } from 'mande'
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'

interface AnnouncementItem {
  slug: string
  title: string
  summary: string
  published_at: string
  read_duration_minutes: number
  category_id: string
  category_title: string
  category_slug: string
  icon_url: string
}

interface AnnouncementList {
  items: AnnouncementItem[]
  page: number
  page_size: number
  total: number
  total_pages: number
}

interface AnnouncementCategory {
  id: string
  title: string
  slug?: string
}

interface AnnouncementItemDetail {
  id: string
  title: string
  published_at: string
  read_duration_minutes: number
  content: string
  similar: object[]
  categories: AnnouncementCategory[]
}

export function useAnnouncementList() {
  const config = useRuntimeConfig()
  const api = mande(`${config.public.apiBaseUrl}/announcement`)
  const language = useI18n().locale.value.split('-')[0]
  const announcementList = ref<AnnouncementList | null>(null)
  const loading = ref<boolean>(true)
  const error = ref<MandeError | null>(null)
  const notFound = ref<boolean>(false)

  const fetchAnnouncementList = async (categoryId: string = '') => {
    try {
      const response = await api.get<{ payload: AnnouncementList }>('', {
        headers: {
          'Accept-Language': language,
        },
        query: categoryId ? { category_id: categoryId } : {},
      })
      announcementList.value = response.payload
      notFound.value = false
    } catch (apiError) {
      const status = (apiError as any)?.response?.status || (apiError as any)?.status
      if (status === 404) {
        announcementList.value = {
          items: [],
          page: 1,
          page_size: 0,
          total: 0,
          total_pages: 1,
        }
        notFound.value = true
      } else {
        error.value = apiError as MandeError
      }
    } finally {
      loading.value = false
    }
  }

  fetchAnnouncementList().then(() => {})

  return {
    announcementList,
    fetchAnnouncementList,
    loading,
    error,
    notFound,
  }
}

export function useAnnouncementCategories() {
  const config = useRuntimeConfig()
  const api = mande(`${config.public.apiBaseUrl}/announcement`)
  const language = useI18n().locale.value.split('-')[0]
  const announcementCategories = ref<AnnouncementCategory[] | null>(null)
  const loading = ref<boolean>(true)
  const error = ref<MandeError | null>(null)

  const fetchAnnouncementCategories = async () => {
    try {
      const response = await api.get<{ payload: AnnouncementCategory[] }>('/categories', {
        headers: {
          'Accept-Language': language,
        },
      })
      announcementCategories.value = response.payload
    } catch (apiError) {
      error.value = apiError as MandeError
    } finally {
      loading.value = false
    }
  }

  fetchAnnouncementCategories().then(() => {})

  return {
    announcementCategories,
    loading,
    error,
  }
}

export function useAnnouncementBySlug(slug: string) {
  const config = useRuntimeConfig()
  const api = mande(`${config.public.apiBaseUrl}/announcement`)
  const language = useI18n().locale.value.split('-')[0]
  const announcement = ref<AnnouncementItemDetail | null>(null)
  const loading = ref<boolean>(true)
  const error = ref<MandeError | null>(null)

  const fetchAnnouncementBySlug = async () => {
    try {
      const response = await api.get<{ payload: AnnouncementItemDetail }>(slug, {
        headers: {
          'Accept-Language': language,
        },
      })
      announcement.value = response.payload
    } catch (apiError) {
      error.value = apiError as MandeError
      // Show Nuxt error page for 404s
      const status = (apiError as any)?.response?.status || (apiError as any)?.status
      if (status === 404) {
        showError({ statusCode: 404 })
      }
    } finally {
      loading.value = false
    }
  }

  fetchAnnouncementBySlug().then(() => {})

  return {
    announcement,
    loading,
    error,
  }
}

export function useLatestAnnouncements() {
  const config = useRuntimeConfig()
  const api = mande(`${config.public.apiBaseUrl}/announcement`)
  const language = useI18n().locale.value.split('-')[0]
  const latestAnnouncements = ref<AnnouncementList | null>(null)
  const loading = ref<boolean>(true)
  const error = ref<MandeError | null>(null)

  const fetchLatestAnnouncements = async () => {
    try {
      const response = await api.get<{ payload: AnnouncementList }>('', {
        headers: {
          'Accept-Language': language,
        },
        query: {
          page: 1,
          page_size: 6,
        },
      })
      latestAnnouncements.value = response.payload
    } catch (apiError) {
      error.value = apiError as MandeError
    } finally {
      loading.value = false
    }
  }

  fetchLatestAnnouncements().then(() => {})

  return {
    latestAnnouncements,
    loading,
    error,
  }
}
